import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  COMPARISON_OPERATORS,
  GROUP_FORMATS,
  GROUP_ALIGNMENTS,
  useQuestionSectionMap,
} from '@/features/quote-and-apply-builder';

interface UseGroupSettingsProps {
  group: PolicyFormQuestionGroup;
  questions: Record<string, PolicyFormQuestion>;
  onSave: (updates: Partial<PolicyFormQuestionGroup>) => void;
  onClose: () => void;
}

export function useGroupSettings({ group, questions, onSave, onClose }: UseGroupSettingsProps) {
  const questionSectionMap = useQuestionSectionMap();
  const [title, setTitle] = useState(group.title || '');
  const [description, setDescription] = useState(group.description || '');
  const [format, setFormat] = useState<'default' | 'grid' | 'inline'>(group.format || 'default');
  const [align, setAlign] = useState<'center' | 'start' | 'end' | undefined>(group.align);
  const [className, setClassName] = useState(group.className || '');
  const [hidden, setHidden] = useState(group.hidden || false);
  const [hasCondition, setHasCondition] = useState(!!group.conditional?.visible);
  const [conditionType, setConditionType] = useState<'simple' | 'logical'>(
    group.conditional?.visible && 'conditions' in group.conditional.visible ? 'logical' : 'simple',
  );
  const [simpleCondition, setSimpleCondition] = useState<FieldCondition>(() => {
    const visible = group.conditional?.visible;
    if (visible && !('conditions' in visible)) {
      return visible as FieldCondition;
    }
    return { field: '', operator: 'equals', value: '' };
  });
  const [logicalCondition, setLogicalCondition] = useState<LogicalCondition>(() => {
    const visible = group.conditional?.visible;
    if (visible && 'conditions' in visible) {
      return visible as LogicalCondition;
    }
    return { operator: 'and', conditions: [] };
  });

  useEffect(() => {
    setTitle(group.title || '');
    setDescription(group.description || '');
    setFormat(group.format || 'default');
    setAlign(group.align);
    setClassName(group.className || '');
    setHidden(group.hidden || false);
    setHasCondition(!!group.conditional?.visible);

    const visible = group.conditional?.visible;
    if (visible) {
      if ('conditions' in visible) {
        setConditionType('logical');
        setLogicalCondition(visible as LogicalCondition);
      } else {
        setConditionType('simple');
        setSimpleCondition(visible as FieldCondition);
      }
    } else {
      setConditionType('simple');
      setSimpleCondition({ field: '', operator: 'equals', value: '' });
      setLogicalCondition({ operator: 'and', conditions: [] });
    }
  }, [group]);

  const questionOptions = useMemo(
    () =>
      Object.values(questions).map(question => ({
        value: question.questionRef,
        label: question.questionRef,
        selectedDisplay: question.questionRef,
        optionDisplay: (
          <div className="flex w-64 flex-col gap-1">
            <span className="text-sm font-medium text-gray-950">{question.questionRef}</span>
            <span className="text-foreground truncate text-xs">{question.title}</span>
            {questionSectionMap[question.questionRef] && (
              <span className="text-primary truncate text-xs">
                {questionSectionMap[question.questionRef]}
              </span>
            )}
          </div>
        ),
      })),
    [questions, questionSectionMap],
  );

  const selectedQuestion = questions[simpleCondition.field];
  const showValueInput = !['empty', 'notEmpty'].includes(simpleCondition.operator);

  const handleSave = useCallback(() => {
    let conditional: ConditionalBehavior | undefined;

    if (hasCondition) {
      if (conditionType === 'simple' && simpleCondition.field) {
        conditional = { visible: simpleCondition };
      } else if (conditionType === 'logical' && logicalCondition.conditions.length > 0) {
        conditional = { visible: logicalCondition };
      }
    }

    onSave({
      title: title || undefined,
      description: description || undefined,
      format,
      align,
      className: className || undefined,
      hidden: hidden || undefined,
      conditional,
    });
    onClose();
  }, [
    hasCondition,
    conditionType,
    simpleCondition,
    logicalCondition,
    title,
    description,
    format,
    align,
    className,
    hidden,
    onSave,
    onClose,
  ]);

  const handleAddLogicalCondition = useCallback(() => {
    setLogicalCondition(prev => ({
      ...prev,
      conditions: [
        ...prev.conditions,
        { field: '', operator: 'equals', value: '' } as FieldCondition,
      ],
    }));
  }, []);

  const handleUpdateLogicalCondition = useCallback(
    (index: number, updates: Partial<FieldCondition>) => {
      setLogicalCondition(prev => ({
        ...prev,
        conditions: prev.conditions.map((condition, conditionIndex) =>
          conditionIndex === index ? { ...condition, ...updates } : condition,
        ) as (FieldCondition | LogicalCondition)[],
      }));
    },
    [],
  );

  const handleRemoveLogicalCondition = useCallback((index: number) => {
    setLogicalCondition(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, conditionIndex) => conditionIndex !== index),
    }));
  }, []);

  return {
    title,
    setTitle,
    description,
    setDescription,
    format,
    setFormat,
    align,
    setAlign,
    className,
    setClassName,
    hidden,
    setHidden,
    hasCondition,
    setHasCondition,
    conditionType,
    setConditionType,
    simpleCondition,
    setSimpleCondition,
    logicalCondition,
    setLogicalCondition,
    questionOptions,
    selectedQuestion,
    showValueInput,
    comparisonOperators: COMPARISON_OPERATORS,
    groupFormats: GROUP_FORMATS,
    groupAlignments: GROUP_ALIGNMENTS,
    handleSave,
    handleAddLogicalCondition,
    handleUpdateLogicalCondition,
    handleRemoveLogicalCondition,
  };
}
